import * as React from 'react';
import './Admin.less';
import { Form, Input, Button, Select, Divider, message, Spin, Checkbox, Tooltip, InputNumber, Row, Col, Upload, Icon } from 'antd';
import bindAllOfThis from "src/utils/BindThisHelper"; // 'this' binder
import Routes from 'src/core/Routes';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { PageProps } from 'src/models/common/ComponentProps';
import AddActivityQuestionResponseDTO from 'src/models/activity/AddActivityQuestionResponseDTO';
import UpdateActivityQuestionResponseDTO from 'src/models/activity/UpdateActivityQuestionResponseDTO';
import ActivityApiController from 'src/api/ActivityApiController';
import Languages from 'src/constants/Languages';
import UpdateActivityQuestionResponseTranslationDTO from "src/models/activity/UpdateActivityQuestionResponseTranslationDTO";
import ActivityQuestionResponseForm, { ActivityQuestionResponseFormReturn, ActivityQuestionResponseFormProps } from "src/pages/admin/forms/AddEditActivityQuestionResponseForm";
import { AddEditQuestionResponseDTO, AddEditQuestionResponseTranslationDTO, CultureFileUploadDTO } from "src/models/frontend/admin/AddEditQuestionResponseDTO";

interface RouteParams {
    activityId: string;
    activityQuestionId: string;
    activityQuestionResponseId: string;
}

interface AddEditActivityQuestionResponseProps extends PageProps<RouteParams>, LocalizeContextProps { }

interface AddEditActivityQuestionResponseState {
    isLoading: boolean;
    isSubmitting: boolean;
    /** Indicates that we are updating an existing item */
    isUpdate: boolean;
    currentActivityQuestionResponse: AddEditQuestionResponseDTO;

    activityId: number;
}

class AddEditActivityQuestionResponse extends React.Component<AddEditActivityQuestionResponseProps, AddEditActivityQuestionResponseState> {
    constructor(props: AddEditActivityQuestionResponseProps) {
        super(props);
        bindAllOfThis(this, AddEditActivityQuestionResponse.prototype);

        let activityId = parseInt(this.props.match.params.activityId, 10);
        let activityQuestionId = parseInt(this.props.match.params.activityQuestionId, 10);
        let activityQuestionResponseId = parseInt(this.props.match.params.activityQuestionResponseId, 10);

        let currentActivityQuestionResponse: AddEditQuestionResponseDTO = {
            activityQuestionResponseId: activityQuestionResponseId,
            activityQuestionId: activityQuestionId,
            translations: [],
            isSurvey: false,
            isCorrect: false,
            weight: null,
            displayOrder: null,
        };

        this.state = {
            isLoading: true,
            isSubmitting: false,
            isUpdate: false,
            activityId: activityId,
            currentActivityQuestionResponse: currentActivityQuestionResponse
        };
    }

    componentDidMount() {
        let activityQuestionId = parseInt(this.props.match.params.activityQuestionId, 10);
        let activityQuestionResponseId = parseInt(this.props.match.params.activityQuestionResponseId, 10);

        if (activityQuestionResponseId != null && activityQuestionResponseId > 0) {
            // We are updating, load previous
            ActivityApiController.getActivityQuestionResponse(activityQuestionResponseId).then(result => {
                if (result.ok) {
                    let translations = result.data.translations.map<AddEditQuestionResponseTranslationDTO>(x => {
                        return {
                            ...x,
                        };
                    });
                    let response: AddEditQuestionResponseDTO = {
                        ...result.data,
                        translations: translations,
                    };

                    this.setState({
                        isLoading: false,
                        isUpdate: true,
                        currentActivityQuestionResponse: response
                    });
                } else {
                    message.error(this.props.translate("admin.addEditActivityQuestionResponse.messageRetrieveError"));
                }
            });
        } else {
            // New response, so get the question and merge information
            let availableLanguages = Languages.All.filter(x => x.isAvailable);
            ActivityApiController.getActivityQuestion(activityQuestionId).then((result) => {
                if (result.ok) {
                    let translations = availableLanguages.map<AddEditQuestionResponseTranslationDTO>(lang => {
                        return {
                            cultureId: lang.id,
                            text: "",
                        };
                    });
                    let response: AddEditQuestionResponseDTO = {
                        ...result.data,
                        activityQuestionResponseId: 0,
                        isCorrect: false,
                        weight: 0,
                        displayOrder: 0,
                        translations: translations,
                    };

                    this.setState({
                        isLoading: false,
                        isUpdate: false,
                        currentActivityQuestionResponse: response
                    });
                } else {
                    message.error(this.props.translate("admin.addEditActivityQuestionResponse.messageRetrieveError"));
                }
            });
        }
    }

    private addActivityQuestionResponse(values: AddEditQuestionResponseDTO, fileList: CultureFileUploadDTO[]): void {
        let addActivityQuestionResponseDTO: AddActivityQuestionResponseDTO = {
            displayOrder: this.state.currentActivityQuestionResponse.displayOrder,
            translations: this.state.currentActivityQuestionResponse.translations as any,
            activityQuestionId: this.state.currentActivityQuestionResponse.activityQuestionId,
            weight: this.state.currentActivityQuestionResponse.weight,
            isCorrect: this.state.currentActivityQuestionResponse.isCorrect
        };

        ActivityApiController.addActivityQuestionResponse()
            .then(result => {
                message.success(this.props.translate('admin.addEditActivityQuestionResponse.messageAdd'));
                this.setState({ isSubmitting: false });
                this.props.history.push(Routes.getRoute(Routes.ADMIN_ADD_EDIT_ACTIVITY_QUESTION, this.state.activityId, this.state.currentActivityQuestionResponse.activityQuestionId));
            }).catch(e => {
                message.error(this.props.translate('admin.addEditActivityQuestionResponse.messageAddError'));
                this.setState({ isSubmitting: false });
            });
    }

    // This has been updated for a form based submission
    private updateActivityQuestionResponse(values: AddEditQuestionResponseDTO, fileList: CultureFileUploadDTO[]): void {
        const formData = new FormData();
        var json = JSON.stringify(values);
        formData.append('jsonStringifiedObject', json);

        // Filter by status as it is null on a new UploadFile object
        fileList.filter(x => x.file != null && x.file.status == null).forEach(file => {
            formData.append('Files[]', file.file as any);
        });

        ActivityApiController.updateActivityQuestionResponse({ formData: formData }).then(result => {
            this.setState({ isSubmitting: false });
            if (result.ok) {
                message.success(this.props.translate("admin.addEditActivityQuestionResponse.messageAdd"));
                this.props.history.push(Routes.getRoute(Routes.ADMIN_ADD_EDIT_ACTIVITY_QUESTION, this.state.activityId, this.state.currentActivityQuestionResponse.activityQuestionId));
                return;
            }

            message.error(this.props.translate("admin.addEditActivityQuestionResponse.messageAddError"));
        });
    }

    onSubmit(values: ActivityQuestionResponseFormReturn) {
        let cleanedValues: AddEditQuestionResponseDTO = {
            // These values are from the load operation
            ...this.state.currentActivityQuestionResponse,

            // Values updated from the form
            displayOrder: values.displayOrder,
            isCorrect: values.isCorrect,
            weight: values.weight,
            translations: values.translations.map<AddEditQuestionResponseTranslationDTO>(x => {
                let cultureFile = values.fileList.find(y => y.cultureId === x.cultureId);
                return {
                    ...x,
                    imageFileName: cultureFile.file != null ? cultureFile.file.name : "",
                };
            }),
        };
        const { fileList } = values;
        const formData = new FormData();
        var json = JSON.stringify(cleanedValues);
        formData.append('jsonStringifiedObject', json);

        // Filter by status as it is null on a new UploadFile object
        fileList.filter(x => x.file != null && x.file.status == null).forEach(file => {
            formData.append('Files[]', file.file as any);
        });

        this.setState({ isSubmitting: true });
        if (this.state.isUpdate) {
            return ActivityApiController.updateActivityQuestionResponse({ formData: formData }).then(result => {
                this.setState({ isSubmitting: false });
                if (result.ok) {
                    message.success(this.props.translate("admin.addEditActivityQuestionResponse.messageUpdate"));
                    this.props.history.push(Routes.getRoute(Routes.ADMIN_ADD_EDIT_ACTIVITY_QUESTION, this.state.activityId, this.state.currentActivityQuestionResponse.activityQuestionId));
                    return;
                }

                message.error(this.props.translate("admin.addEditActivityQuestionResponse.messageUpdateError"));
            });
        }
        return ActivityApiController.addActivityQuestionResponse({ formData: formData }).then(result => {
            this.setState({ isSubmitting: false });
            if (result.ok) {
                message.success(this.props.translate("admin.addEditActivityQuestionResponse.messageAdd"));
                this.props.history.push(Routes.getRoute(Routes.ADMIN_ADD_EDIT_ACTIVITY_QUESTION, this.state.activityId, this.state.currentActivityQuestionResponse.activityQuestionId));
                return;
            }

            message.error(this.props.translate("admin.addEditActivityQuestionResponse.messageAddError"));
        });
    }

    onCancel(): void {
        this.props.history.push(Routes.getRoute(Routes.ADMIN_ADD_EDIT_ACTIVITY_QUESTION, this.state.activityId, this.state.currentActivityQuestionResponse.activityQuestionId));
    }

    render() {
        if (this.state.isLoading) {
            return <Spin />;
        }

        let formProps = {
            currentValues: this.state.currentActivityQuestionResponse,
            onSubmit: this.onSubmit,
            onCancel: this.onCancel,
        } as ActivityQuestionResponseFormProps;

        return (
            <div>
                <h2>
                    <Translate id="admin.addEditActivityQuestionResponse.addHeader" />
                </h2>

                <ActivityQuestionResponseForm {...formProps} />
            </div>
        );
    }
}

export default withLocalize(AddEditActivityQuestionResponse);



// WEBPACK FOOTER //
// ./src/pages/admin/AddEditActivityQuestionResponse.tsx